import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Input,
  Table,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Modal,
  ModalHeader,
  ModalBody,
  Badge
} from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { usePocketBase } from "../../../PocketBaseContext";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import dayjs from "dayjs";

const InvoiceList = () => {
  document.title = "Teklif Listesi | Reform Panel";
  const pb = usePocketBase();
  const navigate = useNavigate();
  //date 
  const formatDate = (date) => {
    if (!date) return "-";
    return dayjs(date).format("DD/MM/YYYY");
  };
  // State tanımlamaları
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [versionHistoryModal, setVersionHistoryModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [versionHistory, setVersionHistory] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);

  // Helper fonksiyonlar
  const renderCustomerInfo = (invoice) => {
    if (!invoice.customerInfo) {
      return "-"; // Müşteri bilgisi yoksa bir tire göster
    }

    return (
      <div>
        <Link
          to={`/reform-customers/detail/${invoice.customerInfo.id}`}
          className="text-primary"
          style={{ textDecoration: "none" }}
        >
          {invoice.customerInfo.name}
        </Link>
        <br />
        <small className="text-muted">
          {invoice.customerInfo.customer_address || "Adres belirtilmemiş"}
        </small>
      </div>
    );
  };


  const formatCurrency = (amount) => {
    if (!amount) return "0,00 ₺";
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
    }).format(amount);
  };

  // fetchInvoices fonksiyonu
  const fetchInvoices = async () => {
    try {
      setLoading(true);

      // Base PocketBase sorgusu (filter ve sort kaldırıldı)
      const resultList = await pb.collection("invoices").getList(1, 50);

      // JSON alanlarını parse et
      const parsedItems = (resultList.items || []).map(item => ({
        ...item,
        total_amount: typeof item.total_amount === 'string' ? JSON.parse(item.total_amount) : item.total_amount,
        products: typeof item.products === 'string' ? JSON.parse(item.products) : item.products,
        notes: typeof item.notes === 'string' ? JSON.parse(item.notes) : item.notes
      }));

      // Faturaları grupla
      const groupedInvoices = groupByOriginalInvoice(parsedItems);

      // Benzersiz müşteri ID'lerini topla
      const customerIds = [...new Set(
        groupedInvoices
          .filter(invoice => invoice?.customer) // null check ekle
          .map(invoice => invoice.customer)
      )];

      // Eğer müşteri ID'leri varsa müşteri verilerini getir
      if (customerIds.length > 0) {
        try {
          const customersData = await Promise.all(
            customerIds.map(id =>
              pb.collection("customers")
                .getOne(id)
                .catch(err => {
                  console.warn(`Customer fetch error for ID ${id}:`, err);
                  return null;
                })
            )
          );

          // Müşteri verilerini map'le
          const customerMap = customersData.reduce((acc, customer) => {
            if (customer) {
              acc[customer.id] = customer;
            }
            return acc;
          }, {});

          // Faturaları müşteri bilgileriyle birleştir
          const invoicesWithCustomers = groupedInvoices.map(invoice => ({
            ...invoice,
            customerInfo: invoice.customer ? customerMap[invoice.customer] : null
          }));

          setInvoices(invoicesWithCustomers);
        } catch (error) {
          console.error("Error fetching customer data:", error);
          setInvoices(groupedInvoices);
        }
      } else {
        setInvoices(groupedInvoices);
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  // fetchVersionHistory fonksiyonu
  const fetchVersionHistory = async (originalId) => {
    try {
      if (!originalId) {
        console.warn('No originalId provided for version history');
        return [];
      }

      // Filter ve sort kaldırıldı
      const result = await pb.collection("invoices").getList(1, 100);

      if (!result?.items?.length) {
        return [];
      }

      const customerIds = [...new Set(
        result.items
          .filter(version => version?.customer)
          .map(version => version.customer)
      )];

      if (customerIds.length === 0) {
        return result.items;
      }

      try {
        const customersData = await Promise.all(
          customerIds.map(id =>
            pb.collection("customers")
              .getOne(id)
              .catch(err => {
                console.warn(`Customer fetch error in version history for ID ${id}:`, err);
                return null;
              })
          )
        );

        const customerMap = customersData.reduce((acc, customer) => {
          if (customer) {
            acc[customer.id] = customer;
          }
          return acc;
        }, {});

        return result.items.map(version => ({
          ...version,
          customerInfo: version.customer ? customerMap[version.customer] : null
        }));
      } catch (error) {
        console.error("Error fetching customers for version history:", error);
        return result.items;
      }
    } catch (error) {
      console.error("Error fetching version history:", error);
      return [];
    }
  };


  // Gruplandırma fonksiyonu
  const groupByOriginalInvoice = (items) => {
    const groups = {};

    items.forEach(invoice => {
      const key = invoice.original_invoice || invoice.id;
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(invoice);
    });

    return Object.values(groups).map(group => {
      return group.sort((a, b) => {
        const versionDiff = (b.version || 0) - (a.version || 0);
        if (versionDiff !== 0) return versionDiff;
        return new Date(b.created) - new Date(a.created);
      })[0];
    });
  };

  const handleDelete = (invoice) => {
    setInvoiceToDelete(invoice);
    setDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (invoiceToDelete) {
      try {
        await pb.collection("invoices").delete(invoiceToDelete.id);
        toast.success("Teklif başarıyla silindi");
        setDeleteModal(false);
        setInvoiceToDelete(null);
        fetchInvoices();
      } catch (error) {
        console.error("Error deleting invoice:", error);
        toast.error("Teklif silinirken bir hata oluştu");
      }
    }
  };



  // Event handlers
  const handleVersionHistoryClick = async (invoice) => {
    setSelectedInvoice(invoice);
    const originalId = invoice.original_invoice || invoice.id;
    const history = await fetchVersionHistory(originalId);
    setVersionHistory(history);
    setVersionHistoryModal(true);
  };

  // useEffect hooks
  useEffect(() => {
    const delaySearch = setTimeout(() => {
      fetchInvoices();
    }, 500);
    return () => clearTimeout(delaySearch);
  }, [searchTerm]);

  useEffect(() => {
    fetchInvoices();
  }, []);

  // Status badge rengi için helper
  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'Taslak': return 'secondary';
      case 'Beklemede': return 'warning';
      case 'Onaylandı': return 'success';
      case 'Reddedildi': return 'danger';
      case 'İptal Edildi': return 'dark';
      default: return 'info';
    }
  };


  // Render
  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Teklif Listesi" pageTitle="Teklifler" />
        <Row>
          <Col>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="search-box">
                    <Input
                      type="text"
                      className="form-control search"
                      placeholder="Teklif no veya başlık ile ara..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{ width: "300px" }}
                    />
                  </div>
                  <Link to="/reform-invoices/detail" className="btn btn-success">
                    <i className="ri-add-line align-bottom me-1"></i> Yeni Teklif
                  </Link>
                </div>

                {loading ? (
                  <div className="text-center p-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Yükleniyor...</span>
                    </div>
                  </div>
                ) : invoices.length === 0 ? (
                  <div className="text-center p-4">
                    <h4>Teklif Bulunamadı</h4>
                    <p className="text-muted">
                      {searchTerm
                        ? "Arama kriterlerine uygun teklif bulunamadı."
                        : "Henüz hiç teklif oluşturulmamış."}
                    </p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table className="table-hover table-nowrap align-middle mb-0">
                      <thead>
                        <tr>
                          <th>Teklif No</th>
                          <th>Başlık</th>
                          <th>Müşteri</th>
                          <th>Tutar (TRY)</th>
                          <th>Durum</th>
                          <th>Ödeme Durumu</th>
                          <th>Son Versiyon</th>
                          <th>Tarih</th>
                          <th>İşlemler</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoices.map((invoice) => (
                          <tr
                            key={invoice.id}
                            onClick={() => navigate(`/reform-invoices/view/${invoice.id}`)}
                            style={{ cursor: 'pointer' }}
                          >
                            <td>{invoice.invoice_number}</td>
                            <td>{invoice.title}</td>
                            <td>{renderCustomerInfo(invoice)}</td>
                            <td>{formatCurrency(invoice.total_amount?.TRY)}</td>
                            <td>
                              <Badge color={getStatusBadgeColor(invoice.status)}>
                                {invoice.status}
                              </Badge>
                            </td>
                            <td>{invoice.payment_status}</td>
                            <td>
                              <Badge
                                color="info"
                                className="cursor-pointer"
                                style={{ cursor: 'pointer' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleVersionHistoryClick(invoice);
                                }}
                              >
                                v{invoice.version || 1}
                              </Badge>
                            </td>
                            <td>{formatDate(invoice.due_date)}</td>
                            <td>
                              <UncontrolledDropdown onClick={(e) => e.stopPropagation()}>
                                <DropdownToggle
                                  tag="button"
                                  className="btn btn-soft-secondary btn-sm"
                                >
                                  <i className="ri-more-fill" />
                                </DropdownToggle>
                                <DropdownMenu className="dropdown-menu-end">
                                  <DropdownItem
                                    onClick={() => navigate(`/reform-invoices/view/${invoice.id}`)}
                                  >
                                    <i className="ri-eye-fill align-bottom me-2 text-muted"></i>
                                    Görüntüle
                                  </DropdownItem>
                                  <DropdownItem
                                    onClick={() => navigate(`/reform-invoices/detail/${invoice.id}`)}
                                  >
                                    <i className="ri-pencil-fill align-bottom me-2 text-muted"></i>
                                    Düzenle
                                  </DropdownItem>
                                  <DropdownItem divider />
                                  <DropdownItem
                                    className="text-danger"
                                    onClick={() => handleDelete(invoice)}
                                  >
                                    <i className="ri-delete-bin-fill align-bottom me-2"></i>
                                    Sil
                                  </DropdownItem>
                                </DropdownMenu>
                              </UncontrolledDropdown>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Version History Modal */}
        <Modal
          isOpen={versionHistoryModal}
          toggle={() => setVersionHistoryModal(false)}
          size="lg"
        >
          <ModalHeader toggle={() => setVersionHistoryModal(false)}>
            Versiyon Geçmişi - {selectedInvoice?.title}
          </ModalHeader>
          <ModalBody>
            <Table className="mb-0">
              <thead>
                <tr>
                  <th>Versiyon</th>
                  <th>Teklif No</th>
                  <th>Müşteri</th>
                  <th>Durum</th>
                  <th>Versiyon Notu</th>
                  <th>Tarih</th>
                  <th>İşlem</th>
                </tr>
              </thead>
              <tbody>
                {versionHistory.map((version) => (
                  <tr key={version.id}>
                    <td>
                      <Badge color="info">v{version.version || 1}</Badge>
                    </td>
                    <td>{version.invoice_number}</td>
                    <td>{renderCustomerInfo(version)}</td>
                    <td>
                      <Badge color={getStatusBadgeColor(version.status)}>
                        {version.status}
                      </Badge>
                    </td>
                    <td>{version.version_note || "-"}</td>
                    <td>
                      {new Date(version.created).toLocaleDateString("tr-TR")}
                    </td>
                    <td>
                      <button
                        className="btn btn-soft-primary btn-sm"
                        onClick={() => {
                          setVersionHistoryModal(false);
                          navigate(`/reform-invoices/view/${version.id}`);
                        }}
                      >
                        <i className="ri-eye-line"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </ModalBody>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          isOpen={deleteModal}
          toggle={() => setDeleteModal(false)}
          centered
        >
          <ModalHeader toggle={() => setDeleteModal(false)}>
            Teklifi Sil
          </ModalHeader>
          <ModalBody className="text-center p-5">
            <div className="text-danger mb-4">
              <i className="ri-delete-bin-line display-4"></i>
            </div>
            <h4>Emin misiniz?</h4>
            <p className="text-muted">
              Bu teklifi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
            </p>
            <div className="hstack gap-2 justify-content-center mt-4">
              <button
                className="btn btn-light"
                onClick={() => setDeleteModal(false)}
              >
                Vazgeç
              </button>
              <button
                className="btn btn-danger"
                onClick={confirmDelete}
              >
                Evet, Sil
              </button>
            </div>
          </ModalBody>
        </Modal>
      </Container>
    </div>
  );
};

export default InvoiceList;